import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Activity, Wind, Zap, AlertTriangle, Sun, Radio, Satellite, Globe, Cloud, Battery, Navigation, Plug, Sparkles } from 'lucide-react';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import { MapContainer, TileLayer,  ImageOverlay } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import toast, { Toaster } from 'react-hot-toast';

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('Error caught in boundary:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-500/20 border-2 border-red-500 text-red-400 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6" />
          <div>
            <p className="font-bold"> Something went wrong</p>
            <p className="text-sm">Please try refreshing the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// API Service
const API = {
  getXrayFlux: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/goes/primary/xrays-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching X-ray data');
      return [];
    }
  },
  getKpIndex: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/planetary_k_index_1m.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Kp data');
      return [];
    }
  },
  getSolarWind: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/solar-wind/plasma-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Solar Wind');
      return null;
    }
  },
  getMagneticField: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/solar-wind/mag-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Magnetic Field');
      return null;
    }
  },
  getSolarWindDetailed: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/solar-wind/plasma-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Detailed Solar Wind');
      return null;
    }
  },
  getSolarCycleIndices: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/text/daily-solar-indices.txt');
      if (!response.ok) throw new Error('Failed to fetch');
      const text = await response.text();
      const lines = text.trim().split('\n');
      
      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line && !line.startsWith('Date') && line.length > 20) {
          const columns = line.split(/\s+/);
          const f107 = columns[2]; 
          return [{ f10_7: f107 || '--' }];
        }
      }
      return [{ f10_7: '--' }];
    } catch (error) {
      toast.error('Error fetching F10.7 data');
      return [{ f10_7: '--' }];
    }
  },
  getSunspotForecast: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/solar-cycle/predicted-solar-cycle.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Sunspot Forecast');
      return [];
    }
  },

  getProtonFlux: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/goes/primary/integral-protons-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Proton Flux');
      return [];
    }
  },
  getElectronFlux: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/goes/primary/differential-electrons-6-hour.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Electron Flux');
      return [];
    }
  },
  getSpaceWeatherScales: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/noaa-scales.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Space Weather Scales');
      return {};
    }
  },
  getAuroraForecast: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/json/ovation_aurora_latest.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Aurora Forecast');
      return null;
    }
  },
  getKyotoDst: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/kyoto-dst.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Kyoto Dst');
      return [];
    }
  },
 getCME: async (startDate, endDate) => {
  try {
    const response = await fetch(
      `https://kauai.ccmc.gsfc.nasa.gov/DONKI/WS/get/CME?startDate=${startDate}&endDate=${endDate}`
    );
    if (!response.ok) throw new Error('API error');
    const data = await response.json();
    return data; // يرجع array من CME events مباشرة
  } catch (error) {
    toast.error('Error fetching CME data');
    return [];
  }
},
  // getSolarCycleIndices: async () => {
  //   try {
  //     const response = await fetch('https://services.swpc.noaa.gov/json/solar-cycle/observed-solar-cycle-indices.json');
  //     return await response.json();
  //   } catch (error) {
  //     toast.error('Error fetching Solar Cycle Indices');
  //     return [];
  //   }
  // },
  getKpForecast: async () => {
    try {
      const response = await fetch('https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json');
      return await response.json();
    } catch (error) {
      toast.error('Error fetching Kp Forecast');
      return [];
    }
  },
};

const useSpaceWeatherData = (historical = false, startDate = null, endDate = null) => {
  const [xrayData, setXrayData] = useState([]);
  const [kpData, setKpData] = useState([]);
  const [kpValue, setKpValue] = useState('--');
  const [kpForecastData, setKpForecastData] = useState([]);
  const [flareClass, setFlareClass] = useState('');
  const [solarWind, setSolarWind] = useState('--');
  const [solarWindDensity, setSolarWindDensity] = useState('--');
  const [solarWindTemp, setSolarWindTemp] = useState('--');
  const [magneticField, setMagneticField] = useState('--');
  const [protonFlux, setProtonFlux] = useState('--');
  const [electronFlux, setElectronFlux] = useState('--');
  const [sunspotForecast, setSunspotForecast] = useState('--');
  const [scales, setScales] = useState({ R: {}, S: {}, G: {} });
  const [auroraReport, setAuroraReport] = useState(null);
  const [dstData, setDstData] = useState([]);
  const [dstValue, setDstValue] = useState('--');
  const [cmeData, setCmeData] = useState([]);
  const [f107Value, setF107Value] = useState('--');
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [prevKpValue, setPrevKpValue] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const xrayResponse = await API.getXrayFlux();
    if (xrayResponse.length > 0) {
      const chartData = xrayResponse.slice(-50).map((item, index) => ({
        time: index % 10 === 0 ? new Date(item.time_tag).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
        flux: parseFloat(item.flux) || 0,
      }));
      setXrayData(chartData);
      const latestFlux = parseFloat(xrayResponse[xrayResponse.length - 1].flux);
      if (latestFlux > 0.0001) setFlareClass('X');
      else if (latestFlux > 0.00001) setFlareClass('M');
      else if (latestFlux > 0.000001) setFlareClass('C');
      else setFlareClass('A/B');
    }

    const kpResponse = await API.getKpIndex();
    if (kpResponse.length > 0) {
      const latestKp = kpResponse[kpResponse.length - 1];
      setKpValue(latestKp.kp_index || '--');
      const kpChartData = kpResponse.slice(-24).map((item, index) => ({
        time: index % 4 === 0 ? new Date(item.time_tag).toLocaleTimeString('en-US', { hour: '2-digit' }) : '',
        kp: parseFloat(item.kp_index) || 0,
      }));
      setKpData(kpChartData);
    }

    const kpForecastResponse = await API.getKpForecast();
    if (kpForecastResponse.length > 1) {
      const forecastData = kpForecastResponse.slice(1).filter(item => item[2] === 'predicted').map(item => ({
        time: new Date(item[0]).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        kp: parseFloat(item[1]) || 0,
      }));
      setKpForecastData(forecastData);
    }

    const windSummary = await API.getSolarWind();
    if (windSummary && windSummary.length > 1) {
      setSolarWind(windSummary[1][0] || '--');
    }

    // const windDetailed = await API.getSolarWindDetailed();
    // if (windDetailed && windDetailed.length > 1) {
    //   const latestWind = windDetailed[windDetailed.length - 1];
    //   setSolarWindDensity(latestWind[1] || '--');
    //   setSolarWindTemp(latestWind[3] || '--');
    // }

    // const magData = await API.getMagneticField();
    // if (magData && magData.length > 1) {
    //   setMagneticField(magData[1][0] || '--');
    // }
        const windDetailed = await API.getSolarWindDetailed();
    if (windDetailed && windDetailed.length > 1) {
      const latestWind = windDetailed[windDetailed.length - 1];
      setSolarWind(latestWind[2] || '--');         
      setSolarWindDensity(latestWind[1] || '--');  
      setSolarWindTemp(latestWind[3] || '--');     
    }

    const magData = await API.getMagneticField();
    if (magData && magData.length > 1) {
      const latestMag = magData[magData.length - 1];
      setMagneticField(latestMag[1] || '--');     
   
    }

    // F10.7
    // const solarCycleData = await API.getSolarCycleIndices();
    // setF107Value(solarCycleData[0]?.f10_7 || '--');

    const protonData = await API.getProtonFlux();
    if (protonData.length > 0) {
      const latestProton = protonData.find(item => item.energy === '>=10 MeV') || protonData[protonData.length - 1];
      setProtonFlux(latestProton ? latestProton.flux.toFixed(2) : '--');
    }

    const electronData = await API.getElectronFlux();
    if (electronData.length > 0) {
      const latestElectron = electronData[electronData.length - 1];
      setElectronFlux(latestElectron ? latestElectron.flux.toFixed(0) : '--');
    }

    const sunspotData = await API.getSunspotForecast();
    if (sunspotData.length > 0) {
      const latestSunspot = sunspotData[sunspotData.length - 1];
      setSunspotForecast(latestSunspot.predicted_ssn || '--');
    }

    const scalesData = await API.getSpaceWeatherScales();
    if (scalesData && scalesData['0']) {
      setScales({
        R: scalesData['0'].R,
        S: scalesData['0'].S,
        G: scalesData['0'].G,
      });
    }

    const auroraData = await API.getAuroraForecast();
    if (auroraData) {
      setAuroraReport(auroraData);
    }

const dstResponse = await API.getKyotoDst();
if (Array.isArray(dstResponse) && dstResponse.length > 0) {
  
  if (Array.isArray(dstResponse[0])) {
    const dstChartData = dstResponse.slice(-24).map(([timeStr, dstStr]) => ({
      time: new Date(timeStr).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      }),
      dst: parseFloat(dstStr) || 0,
    }));
    setDstData(dstChartData);
    setDstValue(dstChartData.length > 0 ? dstChartData[dstChartData.length - 1].dst.toString() : '--');
  } else {

    const dstChartData = dstResponse.slice(-24).map(item => ({
      time: new Date(item.time_tag).toLocaleTimeString('en-US', { hour: '2-digit' }),
      dst: parseFloat(item.dst) || 0,
    }));
    setDstData(dstChartData);
    setDstValue(dstResponse[dstResponse.length - 1]?.dst || '--');
  }
}

    const today = new Date();
    const start = historical ? (startDate || new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0]) : new Date(today.setDate(today.getDate() - 1)).toISOString().split('T')[0];
    const end = endDate || new Date().toISOString().split('T')[0];
    const cmeResponse = await API.getCME(start, end);
    setCmeData(cmeResponse);

        const solarCycleData = await API.getSolarCycleIndices();
    if (solarCycleData && solarCycleData[0]) {
      setF107Value(solarCycleData[0].f10_7 || '--');
    }

    setLoading(false);
    setLastUpdate(new Date());
  }, [historical, startDate, endDate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [fetchData]);

  return {
    xrayData,
    kpData,
    kpValue,
    kpForecastData,
    flareClass,
    solarWind,
    solarWindDensity,
    solarWindTemp,
    magneticField,
    protonFlux,
    electronFlux,
    sunspotForecast,
    scales,
    auroraReport,
    dstData,
    dstValue,
    cmeData,
    f107Value,
    loading,
    lastUpdate,
    prevKpValue,
    setPrevKpValue,
    fetchData,
  };
};

const useEarthImpacts = (scales, kpValue, auroraReport, dstValue) => {
  return useMemo(() => {
    const impacts = [];

    const rScale = parseInt(scales.R.Scale) || 0;
    let hfStatus = 'normal';
    let hfDesc = 'No significant disruptions.';
    if (rScale >= 1) { hfStatus = 'warning'; hfDesc = 'Weak degradation on sunlit side.'; }
    if (rScale >= 3) { hfStatus = 'alert'; hfDesc = 'Wide area blackout of HF radio.'; }
    impacts.push({ title: 'HF Radio Communications', description: hfDesc, status: hfStatus, type: hfStatus });

    const sScale = parseInt(scales.S.Scale) || 0;
    let gpsStatus = 'normal';
    let gpsDesc = 'Normal operation.';
    if (sScale >= 1) { gpsStatus = 'warning'; gpsDesc = 'Intermittent satellite navigation issues.'; }
    if (sScale >= 3) { gpsStatus = 'alert'; gpsDesc = 'Degraded GPS signals at high latitudes.'; }
    impacts.push({ title: 'GPS Systems', description: gpsDesc, status: gpsStatus, type: gpsStatus });

    const gScale = parseInt(scales.G.Scale) || 0;
    let powerStatus = 'normal';
    let powerDesc = 'No impacts.';
    if (gScale >= 1) { powerStatus = 'warning'; powerDesc = 'Possible voltage fluctuations.'; }
    if (gScale >= 3) { powerStatus = 'alert'; powerDesc = 'Widespread voltage control problems.'; }
    impacts.push({ title: 'Power Grids', description: powerDesc, status: powerStatus, type: powerStatus });

    let auroraDesc = auroraReport ? `Forecast: ${auroraReport.forecast || 'Low activity'}` : 'Low activity';
    impacts.push({ title: 'Aurora Reports', description: auroraDesc, status: parseFloat(kpValue) >= 5 ? 'alert' : 'normal', type: 'high' });

    const dst = parseFloat(dstValue);
    let dstStatus = 'normal';
    let dstDesc = 'No geomagnetic disturbances.';
    if (dst <= -50) { dstStatus = 'warning'; dstDesc = 'Moderate geomagnetic storm detected.'; }
    if (dst <= -100) { dstStatus = 'alert'; dstDesc = 'Severe geomagnetic storm affecting systems.'; }
    impacts.push({ title: 'Dst Index Impact', description: dstDesc, status: dstStatus, type: dstStatus });

    return impacts;
  }, [scales, kpValue, auroraReport, dstValue]);
};

// Alert Banner Component
const AlertBanner = React.memo(({ flareClass, severity, kpValue, radiationLevel, impacts, dstValue }) => {
  const alerts = useMemo(() => {
    const result = [];

    if (flareClass && (severity === 'high' || severity === 'medium')) {
      result.push({
        type: severity,
        icon: Zap,
        title: 'Solar Flare Detected',
        message: `Class ${flareClass} - May affect radio communications`,
      });
    }

    if (parseFloat(kpValue) >= 5) {
      result.push({
        type: 'high',
        icon: Globe,
        title: 'Geomagnetic Storm',
        message: `Kp Index: ${kpValue} - Aurora may be visible at lower latitudes`,
      });
    }

    if (radiationLevel && parseInt(radiationLevel.Scale) > 0) {
      result.push({
        type: 'high',
        icon: AlertTriangle,
        title: 'Radiation Storm',
        message: `Level S${radiationLevel.Scale} - Potential impacts on satellites and aviation`,
      });
    }

    if (parseFloat(dstValue) <= -100) {
      result.push({
        type: 'high',
        icon: AlertTriangle,
        title: 'Severe Geomagnetic Storm',
        message: `Dst Index: ${dstValue} nT - Potential power grid issues`,
      });
    }

    if (impacts && impacts.length > 0) {
      impacts.forEach(impact => {
        result.push({
          type: impact.type,
          icon: AlertTriangle,
          title: impact.title,
          message: impact.description,
        });
      });
    }

    return result;
  }, [flareClass, severity, kpValue, radiationLevel, impacts, dstValue]);

  if (alerts.length === 0) return null;

  const colors = {
    high: 'bg-red-500/20 border-red-500 text-red-400',
    medium: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    normal: 'bg-green-500/20 border-green-500 text-green-400',
  };

  return (
    <ErrorBoundary>
      <div className="space-y-3 mb-6">
        {alerts.map((alert, index) => {
          const Icon = alert.icon;
          return (
            <div key={index} className={`${colors[alert.type] || colors.normal} border-2 rounded-lg p-4 flex items-center gap-3 animate-pulse`}>
              <Icon className="w-6 h-6" />
              <div>
                <p className="font-bold">⚠️ {alert.title}</p>
                <p className="text-sm">{alert.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </ErrorBoundary>
  );
});


const StatCard = React.memo(({ icon: Icon, title, value, unit, status, subtitle }) => {
  const statusColors = {
    normal: 'text-green-400',
    warning: 'text-yellow-400',
    alert: 'text-red-400',
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-cyan-400" />
        <h3 className="text-slate-400 text-sm font-medium">{title}</h3>
      </div>
      <div className={`text-3xl font-bold ${statusColors[status] || 'text-white'}`}>
        {value}
        {unit && <span className="text-lg text-slate-400 ml-2">{unit}</span>}
      </div>
      {subtitle && <p className="text-xs text-slate-500 mt-2">{subtitle}</p>}
    </div>
  );
});


const SolarChart = React.memo(({ data, loading, title, dataKey, color, type = 'line' }) => {
  if (loading) {
    return (
      <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 h-80 flex items-center justify-center">
        <div className="text-slate-400">Loading chart data...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" />
          {title}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          {type === 'area' ? (
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
              <Area type="monotone" dataKey={dataKey} stroke={color} fill={color} fillOpacity={0.3} />
            </AreaChart>
          ) : (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
              <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </ErrorBoundary>
  );
});


const SolarImage = React.memo(({ title, imageUrl, icon: Icon }) => {
  return (
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-cyan-400" />
        {title}
      </h3>
      <div className="aspect-square bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23334155" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%2394a3b8" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
        />
      </div>
      <p className="text-xs text-slate-500 mt-2 text-center">Updated: {new Date().toLocaleTimeString()}</p>
    </div>
  );
});


const ImpactCard = React.memo(({ icon: Icon, title, description, status }) => {
  const statusColors = {
    normal: 'bg-green-500/20 border-green-500 text-green-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    alert: 'bg-red-500/20 border-red-500 text-red-400',
  };

  return (
    <div className={`${statusColors[status]} border-2 rounded-lg p-4 flex items-start gap-3`}>
      <Icon className="w-6 h-6 flex-shrink-0" />
      <div>
        <p className="font-bold">{title}</p>
        <p className="text-sm">{description}</p>
      </div>
    </div>
  );
});


const CMECard = React.memo(({ cme }) => {
  return (
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6">
      <h4 className="font-bold mb-2">CME ID: {cme.activityID || 'N/A'}</h4>
      <p>Start Time: {cme.startTime || 'N/A'}</p>
      <p>Speed: {cme.cmeAnalyses?.[0]?.speed ?? 'N/A'} km/s</p>
      <p>Type: {cme.cmeAnalyses?.[0]?.type ?? 'N/A'}</p>
      <p>Note: {cme.note || 'No additional notes'}</p>
    </div>
  );
});

const AuroraMap = React.memo(({ auroraReport }) => {

  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    const updateImage = () => {
      setImageUrl(
        `https://services.swpc.noaa.gov/images/aurora-forecast-northern-hemisphere.jpg?t=${Date.now()}`
      );
    };
    updateImage();
    const interval = setInterval(updateImage, 1800000); // 30 دقيقة
    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <MapContainer
        center={[70, 0]}
        zoom={2}
        style={{ height: '400px', width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {imageUrl && (
          <ImageOverlay
            url={imageUrl}
            bounds={[[20, -180], [90, 180]]}
            opacity={0.8}
            alt="Aurora Forecast"
          />
        )}
      </MapContainer>
    </ErrorBoundary>
  );
});

const Dashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [historicalStart, setHistoricalStart] = useState('');
  const [historicalEnd, setHistoricalEnd] = useState('');

  const currentData = useSpaceWeatherData(false);
  const historicalData = useSpaceWeatherData(true, historicalStart, historicalEnd);

  const data = tabValue === 0 ? currentData : historicalData;

  const {
    xrayData,
    kpData,
    kpValue,
    kpForecastData,
    flareClass,
    solarWind,
 
    magneticField,
    protonFlux,
    electronFlux,
    sunspotForecast,
    scales,
    auroraReport,
    dstData,
    dstValue,
    cmeData,
    f107Value,
    loading,
    lastUpdate,
    prevKpValue,
    setPrevKpValue,
    fetchData,
  } = data;

  const impacts = useEarthImpacts(scales, kpValue, auroraReport, dstValue);

  const getKpStatus = useCallback(() => {
    if (kpValue === '--') return 'normal';
    const kp = parseFloat(kpValue);
    if (kp >= 5) return 'alert';
    if (kp >= 4) return 'warning';
    return 'normal';
  }, [kpValue]);

  const getSeverity = useCallback(() => {
    if (flareClass === 'X') return 'high';
    if (flareClass === 'M') return 'medium';
    return 'low';
  }, [flareClass]);

  const getRadiationStatus = useCallback(() => {
    const sScale = parseInt(scales.S.Scale) || 0;
    if (sScale >= 3) return 'alert';
    if (sScale >= 1) return 'warning';
    return 'normal';
  }, [scales]);

  const getDstStatus = useCallback(() => {
    if (dstValue === '--') return 'normal';
    const dst = parseFloat(dstValue);
    if (dst <= -100) return 'alert';
    if (dst <= -50) return 'warning';
    return 'normal';
  }, [dstValue]);

  useEffect(() => {
    if (prevKpValue !== null && kpValue !== '--' && parseFloat(kpValue) - parseFloat(prevKpValue) >= 2) {
      toast(`Significant increase in Kp Index: from ${prevKpValue} to ${kpValue}. Potential geomagnetic storm!`, {
        icon: '⚠️',
        style: { background: '#1e293b', color: '#fff', border: '1px solid #334155' },
      });
    }
    setPrevKpValue(kpValue);
  }, [kpValue, prevKpValue, setPrevKpValue]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-4 md:p-6">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto">
    
        <header className="mb-6">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">
            Solar Pulse Dashboard
          </h1>
          <p className="text-slate-400">Real-time Space Weather Monitoring System</p>
          <p className="text-sm text-slate-500 mt-2">
            Last updated: {lastUpdate.toLocaleTimeString()} | Auto-refresh: 5 min
          </p>
        </header>


        <Tabs selectedIndex={tabValue} onSelect={(index) => setTabValue(index)}>
          <TabList className="mb-6 flex gap-4 flex-wrap">
            <Tab className="px-6 py-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-cyan-500/20 transition">Current Data</Tab>
            <Tab className="px-6 py-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-cyan-500/20 transition">Historical Data</Tab>
          </TabList>

          <TabPanel>
            {/* <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={fetchData}
                className="bg-cyan-500 p-2 rounded hover:bg-cyan-600 transition"
              >
                Fetch Current Data
              </button>
            </div> */}
          </TabPanel>
          <TabPanel>
            <div className="flex flex-wrap gap-4 mb-6">
              <input
                type="date"
                value={historicalStart}
                onChange={(e) => setHistoricalStart(e.target.value)}
                className="bg-slate-800 px-4 py-2 rounded text-white"
              />
              <input
                type="date"
                value={historicalEnd}
                onChange={(e) => setHistoricalEnd(e.target.value)}
                className="bg-slate-800 px-4 py-2 rounded text-white"
              />
              <button
                onClick={fetchData}
                className="bg-cyan-500 px-6 py-2 rounded hover:bg-cyan-600 transition"
              >
                Fetch Historical Data
              </button>
            </div>
          </TabPanel>
        </Tabs>

        {/* Alert Banner */}
        <AlertBanner
          flareClass={flareClass}
          severity={getSeverity()}
          kpValue={kpValue}
          radiationLevel={scales.S}
          impacts={impacts.filter(impact => impact.status !== 'normal')}
          dstValue={dstValue}
        />

        {/* Primary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Zap} title="X-ray Flux Class" value={flareClass} status={getSeverity() === 'high' ? 'alert' : getSeverity() === 'medium' ? 'warning' : 'normal'} subtitle="Solar Flare Activity" />
          <StatCard icon={Globe} title="Kp Index" value={kpValue} status={getKpStatus()} subtitle="Geomagnetic Activity" />
          <StatCard icon={Wind} title="Solar Wind Speed" value={solarWind} unit="km/s" status="normal" subtitle="Particle Speed" />
          <StatCard icon={Activity} title="Magnetic Field (Bt)" value={magneticField} unit="nT" status="normal" subtitle="IMF Strength" />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Radio} title="Proton Flux" value={protonFlux} unit="pfu" status="normal" subtitle=">10 MeV" />
          <StatCard icon={Satellite} title="Electron Flux" value={electronFlux} unit="e/cm²/s" status="normal" subtitle="Differential" />
          <StatCard icon={Sun} title="Sunspot Forecast" value={sunspotForecast} status="normal" subtitle="Predicted SSN" />
          <StatCard icon={Battery} title="Radiation Storm" value={scales.S.Scale || '0'} status={getRadiationStatus()} subtitle="S Scale" />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Globe} title="Dst Index" value={dstValue} unit="nT" status={getDstStatus()} subtitle="Geomagnetic Storm Intensity" />
          <StatCard icon={Radio} title="F10.7 Radio Flux" value={f107Value} unit="sfu" status="normal" subtitle="Solar Radio Emission" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SolarChart data={xrayData} loading={loading} title="X-ray Flux (6 Hours)" dataKey="flux" color="#06b6d4" />
          <SolarChart data={kpData} loading={loading} title="Kp Index (24 Hours)" dataKey="kp" color="#f59e0b" type="area" />
          <SolarChart data={kpForecastData} loading={loading} title="Kp Forecast (3 Days)" dataKey="kp" color="#ef4444" type="area" />
          <SolarChart data={dstData} loading={loading} title="Dst Index (24 Hours)" dataKey="dst" color="#3b82f6" />
        </div>

        {/* Solar Images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <SolarImage title="Solar Disk (SDO AIA 193)" imageUrl="https://sdo.gsfc.nasa.gov/assets/img/latest/latest_256_0193.jpg" icon={Sun} />
          <SolarImage title="LASCO C3 Coronagraph" imageUrl="https://services.swpc.noaa.gov/images/animations/lasco-c3/latest.jpg" icon={Satellite} />
          <SolarImage title="Aurora Forecast Image" imageUrl="https://services.swpc.noaa.gov/images/aurora-forecast-northern-hemisphere.jpg" icon={Globe} />
        </div>

        {/* Interactive Aurora Map */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Interactive Aurora Oval Map
          </h3>
          <AuroraMap auroraReport={auroraReport} />
        </div>

        {/* Recent CME Events */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Satellite className="w-5 h-5 text-cyan-400" />
            Recent CME Events (Last 7 Days)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cmeData.length > 0 ? (
              cmeData.slice(-6).reverse().map((cme, index) => <CMECard key={index} cme={cme} />)
            ) : (
              <p className="text-slate-400 col-span-full text-center">No recent CME events detected.</p>
            )}
          </div>
        </div>

        {/* Earth Impacts */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Potential Earth Impacts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {impacts.map((impact, index) => (
              <ImpactCard
                key={index}
                icon={impact.title.includes('HF') ? Radio : impact.title.includes('GPS') ? Navigation : impact.title.includes('Power') ? Plug : impact.title.includes('Aurora') ? Sparkles : Globe}
                title={impact.title}
                description={impact.description}
                status={impact.status}
              />
            ))}
          </div>
        </div>

        {/* Info Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              NOAA Space Weather Scales
            </h3>
            <div className="text-slate-300 space-y-2 text-sm">
              <p><strong className="text-red-400">R5 (Extreme):</strong> Complete HF blackout on sunlit side</p>
              <p><strong className="text-orange-400">S5 (Extreme):</strong> Satellites may fail, navigation degraded</p>
              <p><strong className="text-yellow-400">G5 (Extreme):</strong> Power grid collapse, widespread aurora</p>
            </div>
          </div>
          <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-3">Data Sources</h3>
            <div className="text-slate-300 space-y-2 text-sm">
              <p><strong className="text-cyan-400">GOES Satellites:</strong> X-ray, particles</p>
              <p><strong className="text-cyan-400">ACE/DSCOVR:</strong> Solar wind, IMF</p>
              <p><strong className="text-cyan-400">SDO:</strong> Solar imaging</p>
              <p><strong className="text-cyan-400">NOAA SWPC:</strong> Forecasts & alerts</p>
              <p><strong className="text-cyan-400">NASA DONKI:</strong> CME catalog</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-slate-500 text-sm py-8 border-t border-slate-800">
          <p>Built with React, Recharts, Leaflet & Lucide Icons</p>
          <p className="mt-2">Data from NOAA Space Weather Prediction Center & NASA</p>
          <p className="mt-2">For monitoring purposes </p>
          <p className="mt-2">Built by <a href="https://www.linkedin.com/in/nihal-sabri/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Nihal Sabri</a></p>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;